import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import * as API from '../../lib/api';
import styled from 'styled-components';

import SpinnerComponent from '../Common/Spinners';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';


const BackgroundBlurrer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  ${props => (!props.$showing) ? 'display: none;' : null}
`

const ModalContainer = styled.div`
  position: fixed;
  margin: 0 auto;
  top: 0;
  ${props => props.$fromProfile ? (
    'left: calc(50% + 6em);'
  ) : (
    'left: 50%;'
  )}
  transform: ${props => props.$fromProfile ? (
    'translate(-50%, 75%)'
  ) : (
    'translate(-50%, 25%)'
  )};
  display: ${props => props.$showing ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 1000;
  height: 15rem;
  width: 30em;
  padding: 3px;
  background: white;
  border: 1px solid black;

  @media (max-width: 768px) {
    left: 50%;
    transform: ${props => props.$fromProfile ? (
    'translate(-50%, 50%)'
  ) : (
    'translate(-50%, 25%)'
  )};
    width: 25em;
  }
`

const CloseModal = styled.div`
  padding-left: .2em;
  cursor: pointer;
`

const ModalHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 1.2em;
  font-weight: bold;
  margin: .5em 1px 0 1px;
  padding: 0 3px 3px 3px;
  background: white;
`

const ModalTitle = styled.div`
  flex: 1;
`

const ModalContentsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: .25em 3px;
`

const EditPlayerForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const InputFieldContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  font-size: .9em;
`

const InputLabel = styled.span`
  flex: 1;
  padding-right: .5em;
  font-weight: bold;
`

const FieldInput = styled.input`
  width: 8em;
  padding: 2px;
`

const Comment = styled.span`
  flex: 1.5;
  padding: 0 .5em;
  font-size: .75em;
  font-style: italic;
`

const SubmitButton = styled.button`
  margin-top: .5em;
  padding: .3em 1em;
  max-width: 15em;
  border: 1px solid #ccc;
  text-align: center;
  font-size: 1em;
  cursor: pointer;

  &:hover {
    background: #228fd8;
    color: white;
  }
`

const Warning = styled.div`
  ${props => !props.$show && 'display: none;'}
  margin-top: 1.5em;
  color: #228fd8;
  font-style: italic;
`

const EditPlayerModal = ({ AppStore, showing, playerId, playerInfo }) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const currentRoute = AppStore.currentRoute.get();
  const playerEditBeingWorkedOn = AppStore.playerEditBeingWorkedOn.get();

  const [birthdate, setBirthdate] = useState(playerInfo.birthdate || '');
  const [role, setRole] = useState(playerInfo.role || '');
  const [igl, setIgl] = useState(Boolean(playerInfo.inGameLeader) || false);
  const [region, setRegion] = useState(playerInfo.region || '');
  const [contractEnd, setContractEnd] = useState(playerInfo.contractEnd || '');
  const [warning, setWarning] = useState('');

  const closeModal = (event) => {
    event.preventDefault();
    AppStore.openCloseEditPlayerModal();
  }

  const fetchPlayerProfileData = (async (playerId) => {
    AppStore.setPlayerProfileFetching(true);
    const playerProfileData = await API.getPlayerProfile(playerId);
    AppStore.updatePlayerProfileData(playerId, playerProfileData);
    AppStore.setPlayerProfileInView(playerId);
    AppStore.setPlayerProfileFetching(false);
  })

  const toggleIgl = () => {
    setIgl(!igl);
  }

  const handleSubmit = async () => {
    // Add catch for invalid inputs and raise warnings
    if (birthdate instanceof Date && isNaN(birthdate)) {
      // Not sure how this will handle a blank input, which should be allowed.
      setWarning('ERROR: Not a valid date');
      return;
    }
    if (contractEnd && typeof (contractEnd) !== 'number' && contractEnd < 2023) {
      setWarning('ERROR: Contract end date must be the current year or a future year, or blank');
      return;
    }
    if (!['Duelist', 'Initiator', 'Controller', 'Sentinel', 'Flex', 'Coach', ''].includes(role)) {
      setWarning('ERROR: Not a valid role');
      return;
    }
    if (!['Americas', 'EMEA', 'Pacific', 'China', 'Other', ''].includes(region)) {
      setWarning('ERROR: Not a valid region');
      return;
    }

    const editedPlayerInfo = {
      birthdate: birthdate || null,
      role: role || null,
      inGameLeader: igl || null,
      region: region || null,
      contractEnd: contractEnd || null,
      // contactInfo: contactInfo || null
    }

    await AppStore.applyPlayerEdit(playerId, editedPlayerInfo);
    AppStore.openCloseEditPlayerModal();
    setWarning('');
    fetchPlayerProfileData(playerId);
  }

  useEffect(() => {
  }, [
    AppStore,
    showing,
    playerEditBeingWorkedOn
  ]);

  return (
    <>
      <BackgroundBlurrer
        $showing={showing}
        onClick={closeModal}
      />
      <ModalContainer
        $showing={showing}
        $fromProfile={currentRoute === 'players'}
      >
        <ModalHeaderContainer>
          <ModalTitle>Edit Player Info</ModalTitle>
          <CloseModal onClick={closeModal}>
            <FontAwesomeIcon
              icon={faXmark}
            />
          </CloseModal>
        </ModalHeaderContainer>
        <ModalContentsContainer>
          {playerEditBeingWorkedOn ? (
            <SpinnerComponent />
          ) : (
            <>
              <EditPlayerForm>
                <InputFieldContainer>
                  <InputLabel>IGL</InputLabel>
                  <FieldInput
                    type='checkbox'
                    checked={igl}
                    onChange={() => toggleIgl()}
                  />
                  <Comment>
                    In-game leader? Checked means yes
                  </Comment>
                </InputFieldContainer>
                <InputFieldContainer>
                  <InputLabel>Role</InputLabel>
                  <FieldInput
                    type='text'
                    value={role}
                    onChange={(event) => setRole(event.target.value)}
                  />
                  {/* Should make this a dropdown */}
                  <Comment>
                    Player role, or Coach
                  </Comment>
                </InputFieldContainer>
                <InputFieldContainer>
                  <InputLabel>Region</InputLabel>
                  <FieldInput
                    type='text'
                    value={region}
                    onChange={(event) => setRegion(event.target.value)}
                  />
                  {/* Should make this a dropdown */}
                  <Comment>
                    Americas, EMEA, Pacific, China, Other
                  </Comment>
                </InputFieldContainer>
                <InputFieldContainer>
                  <InputLabel>Birthdate</InputLabel>
                  <FieldInput
                    type='date'
                    value={birthdate}
                    onChange={(event) => setBirthdate(event.target.value)}
                  />
                  <Comment>
                    "yyy-mm-dd" submits blank/unknown
                  </Comment>
                </InputFieldContainer>
                <InputFieldContainer>
                  <InputLabel>Contract End Year</InputLabel>
                  <FieldInput
                    type='number'
                    value={contractEnd}
                    onChange={(event) => setContractEnd(event.target.value)}
                  />
                  <Comment>
                    Must be current or future year, or blank
                  </Comment>
                </InputFieldContainer>
                {/* <InputFieldContainer>
                  <InputLabel>Contact Info</InputLabel>
                  <FieldInput
                    type='text'
                    value={contactInfo}
                    onChange={(event) => setContactInfo(event.target.value)}
                    style={{flex: '1.5', marginRight: 'calc(8.5em + 2px)'}}
                  />
                </InputFieldContainer> */}
              </EditPlayerForm>
              <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              <Warning $show={warning !== ''}>{warning}</Warning>
            </>
          )}
        </ModalContentsContainer>
      </ModalContainer>
    </>
  );
};

export default inject("AppStore")(observer(EditPlayerModal));